import { create } from 'zustand';

export type RelatedFilterType = 'nearby' | 'same_type' | 'same_destination' | 'same_flag' | 'same_owner';
export type RelatedOrderBy = 'recency' | 'distance';

export interface RelatedFiltersState {
  nearby: boolean;
  same_type: boolean;
  same_destination: boolean;
  same_flag: boolean;
  same_owner: boolean;
}

const EMPTY_FILTERS: RelatedFiltersState = {
  nearby: false,
  same_type: false,
  same_destination: false,
  same_flag: false,
  same_owner: false,
};

export interface VesselCoords {
  lat: number;
  lon: number;
}

export interface AreaTrafficBbox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

export interface AreaTrafficVessel {
  mmsi: string;
  name?: string;
  imo?: string;
  ship_type?: number;
  flag_country?: string;
  destination?: string;
  first_ts: string;
  last_ts: string;
  status: 'inside' | 'entered' | 'exited' | 'passed_through';
  avg_sog?: number;
  min_sog?: number;
  max_sog?: number;
  entry_point?: { lat: number; lon: number; ts: string } | null;
  exit_point?: { lat: number; lon: number; ts: string } | null;
  positions: Array<{
    ts: string;
    lat: number;
    lon: number;
    sog?: number;
    cog?: number;
    heading?: number | null;
  }>;
}

export interface AreaTrafficTimelineBucket {
  ts: string;
  count: number;
}

export interface AreaTrafficMetadata {
  stats: {
    vessel_count: number;
    total_positions: number;
    timeline_bucket_hours?: number;
  };
  time_range: {
    start: string;
    end: string;
  };
  bbox: AreaTrafficBbox;
  timeline?: AreaTrafficTimelineBucket[];
  downsampling?: string;
}

export interface DensityCell {
  lat: number;
  lon: number;
  buckets: Array<{ ts: string; count: number }>;
}

export interface DensityHistoryMetadata {
  time_range: { start: string; end: string };
  total_positions: number;
  cell_count: number;
  grid_resolution: number;
  bucket_hours: number;
}

interface CdcState {
  selectedMmsi: string | null;
  setSelectedMmsi: (mmsi: string | null) => void;

  // Selected vessel's current coordinates (updated from bbox data)
  selectedVesselCoords: VesselCoords | null;
  setSelectedVesselCoords: (coords: VesselCoords | null) => void;

  // Animation state
  animatingMmsi: string | null;
  setAnimatingMmsi: (mmsi: string | null) => void;

  // Animation overlay state (for floating controls)
  isAnimationOverlayOpen: boolean;
  animationDurationHours: number;
  setAnimationDurationHours: (hours: number) => void;
  startAnimation: (mmsi: string) => void;
  stopAnimation: () => void;

  // Related vessels filters (supports multiple active filters)
  activeRelatedFilters: RelatedFiltersState;
  toggleRelatedFilter: (filter: RelatedFilterType) => void;
  clearRelatedFilters: () => void;

  // Related vessels ordering
  relatedOrderBy: RelatedOrderBy;
  setRelatedOrderBy: (orderBy: RelatedOrderBy) => void;

  // Nearby filter radius (in kilometers)
  nearbyRadiusKm: number;
  setNearbyRadiusKm: (radius: number) => void;

  // Related vessels limit (for Load All)
  relatedVesselsLimit: number;
  setRelatedVesselsLimit: (limit: number) => void;
  resetRelatedVesselsLimit: () => void;

  // Set of related vessel MMSIs (used to filter from main layer)
  relatedVesselMmsis: Set<string>;
  setRelatedVesselMmsis: (mmsis: Set<string>) => void;

  // Area Traffic Replay state
  isAreaTrafficActive: boolean;
  isAreaTrafficPanelOpen: boolean;
  areaTrafficBbox: AreaTrafficBbox | null;
  areaTrafficDays: number;
  areaTrafficVessels: AreaTrafficVessel[];
  isAreaTrafficLoading: boolean;
  areaTrafficError: string | null;
  areaTrafficMetadata: AreaTrafficMetadata | null;
  isGeofenceModalOpen: boolean;
  isRectangleDrawActive: boolean;
  selectedAreaName: string | null;

  // Area Traffic actions
  openAreaTrafficPanel: () => void;
  closeAreaTrafficPanel: () => void;
  setAreaTrafficBbox: (bbox: AreaTrafficBbox | null, areaName?: string | null) => void;
  setAreaTrafficDays: (days: number) => void;
  setAreaTrafficVessels: (vessels: AreaTrafficVessel[]) => void;
  setAreaTrafficLoading: (loading: boolean) => void;
  setAreaTrafficError: (error: string | null) => void;
  setAreaTrafficMetadata: (metadata: AreaTrafficMetadata | null) => void;
  startAreaTraffic: () => void;
  stopAreaTraffic: () => void;
  openGeofenceModal: () => void;
  closeGeofenceModal: () => void;
  startRectangleDraw: () => void;
  stopRectangleDraw: () => void;

  // Time Machine state
  isTimeMachineActive: boolean;
  timeMachineDays: number;
  timeMachineZoom: number;
  timeMachineVessels: AreaTrafficVessel[];
  timeMachineDensity: DensityCell[];
  timeMachineMetadata: AreaTrafficMetadata | null;
  timeMachineDensityMetadata: DensityHistoryMetadata | null;
  isTimeMachineLoading: boolean;
  timeMachineError: string | null;

  // Time Machine actions
  toggleTimeMachine: () => void;
  setTimeMachineDays: (days: number) => void;
  setTimeMachineZoom: (zoom: number) => void;
  setTimeMachineVessels: (vessels: AreaTrafficVessel[]) => void;
  setTimeMachineDensity: (cells: DensityCell[]) => void;
  setTimeMachineMetadata: (metadata: AreaTrafficMetadata | null) => void;
  setTimeMachineDensityMetadata: (metadata: DensityHistoryMetadata | null) => void;
  setTimeMachineLoading: (loading: boolean) => void;
  setTimeMachineError: (error: string | null) => void;
  stopTimeMachine: () => void;
}

export const useCdcStore = create<CdcState>((set) => ({
  selectedMmsi: null,
  // Don't clear filters when selecting a vessel - allows connection lines to persist
  // when clicking through related vessels. Filters are only cleared explicitly.
  setSelectedMmsi: (mmsi) => set({ selectedMmsi: mmsi, selectedVesselCoords: null }),

  selectedVesselCoords: null,
  setSelectedVesselCoords: (coords) => set({ selectedVesselCoords: coords }),

  animatingMmsi: null,
  setAnimatingMmsi: (mmsi) => set({ animatingMmsi: mmsi }),

  // Animation overlay
  isAnimationOverlayOpen: false,
  animationDurationHours: 6,
  setAnimationDurationHours: (hours) => set({ animationDurationHours: hours }),
  startAnimation: (mmsi) => set({ animatingMmsi: mmsi, isAnimationOverlayOpen: true }),
  stopAnimation: () => set({ animatingMmsi: null, isAnimationOverlayOpen: false }),

  activeRelatedFilters: EMPTY_FILTERS,
  toggleRelatedFilter: (filter) =>
    set((state) => ({
      activeRelatedFilters: {
        ...state.activeRelatedFilters,
        [filter]: !state.activeRelatedFilters[filter],
      },
      // Reset limit when filters change
      relatedVesselsLimit: 100,
    })),
  clearRelatedFilters: () =>
    set({ activeRelatedFilters: EMPTY_FILTERS, relatedVesselsLimit: 100, relatedVesselMmsis: new Set<string>() }),

  relatedOrderBy: 'recency',
  setRelatedOrderBy: (orderBy) => set({ relatedOrderBy: orderBy }),

  nearbyRadiusKm: 50,
  setNearbyRadiusKm: (radius) => set({ nearbyRadiusKm: radius, relatedVesselsLimit: 100 }),

  relatedVesselsLimit: 100,
  setRelatedVesselsLimit: (limit) => set({ relatedVesselsLimit: limit }),
  resetRelatedVesselsLimit: () => set({ relatedVesselsLimit: 100 }),

  relatedVesselMmsis: new Set<string>(),
  setRelatedVesselMmsis: (mmsis) => set({ relatedVesselMmsis: mmsis }),

  // Area Traffic Replay
  isAreaTrafficActive: false,
  isAreaTrafficPanelOpen: false,
  areaTrafficBbox: null,
  areaTrafficDays: 3,
  areaTrafficVessels: [],
  isAreaTrafficLoading: false,
  areaTrafficError: null,
  areaTrafficMetadata: null,
  isGeofenceModalOpen: false,
  isRectangleDrawActive: false,
  selectedAreaName: null,

  openAreaTrafficPanel: () => set({ isAreaTrafficPanelOpen: true }),
  closeAreaTrafficPanel: () =>
    set({
      isAreaTrafficPanelOpen: false,
      isAreaTrafficActive: false,
      areaTrafficBbox: null,
      areaTrafficVessels: [],
      areaTrafficError: null,
      areaTrafficMetadata: null,
      selectedAreaName: null,
    }),
  setAreaTrafficBbox: (bbox, areaName = null) => set({ areaTrafficBbox: bbox, selectedAreaName: areaName }),
  setAreaTrafficDays: (days) => set({ areaTrafficDays: days }),
  setAreaTrafficVessels: (vessels) => set({ areaTrafficVessels: vessels }),
  setAreaTrafficLoading: (loading) => set({ isAreaTrafficLoading: loading }),
  setAreaTrafficError: (error) => set({ areaTrafficError: error }),
  setAreaTrafficMetadata: (metadata) => set({ areaTrafficMetadata: metadata }),
  startAreaTraffic: () => set({ isAreaTrafficActive: true }),
  stopAreaTraffic: () =>
    set({
      isAreaTrafficActive: false,
      areaTrafficVessels: [],
      areaTrafficMetadata: null,
    }),
  openGeofenceModal: () => set({ isGeofenceModalOpen: true }),
  closeGeofenceModal: () => set({ isGeofenceModalOpen: false }),
  startRectangleDraw: () => set({ isRectangleDrawActive: true }),
  stopRectangleDraw: () => set({ isRectangleDrawActive: false }),

  // Time Machine
  isTimeMachineActive: false,
  timeMachineDays: 7,
  timeMachineZoom: 3,
  timeMachineVessels: [],
  timeMachineDensity: [],
  timeMachineMetadata: null,
  timeMachineDensityMetadata: null,
  isTimeMachineLoading: false,
  timeMachineError: null,

  toggleTimeMachine: () =>
    set((state) => {
      if (state.isTimeMachineActive) {
        return {
          isTimeMachineActive: false,
          timeMachineVessels: [],
          timeMachineDensity: [],
          timeMachineMetadata: null,
          timeMachineDensityMetadata: null,
          timeMachineError: null,
        };
      }
      return { isTimeMachineActive: true };
    }),
  setTimeMachineDays: (days) => set({ timeMachineDays: days }),
  setTimeMachineZoom: (zoom) => set({ timeMachineZoom: zoom }),
  setTimeMachineVessels: (vessels) => set({ timeMachineVessels: vessels }),
  setTimeMachineDensity: (cells) => set({ timeMachineDensity: cells }),
  setTimeMachineMetadata: (metadata) => set({ timeMachineMetadata: metadata }),
  setTimeMachineDensityMetadata: (metadata) => set({ timeMachineDensityMetadata: metadata }),
  setTimeMachineLoading: (loading) => set({ isTimeMachineLoading: loading }),
  setTimeMachineError: (error) => set({ timeMachineError: error }),
  stopTimeMachine: () =>
    set({
      isTimeMachineActive: false,
      timeMachineVessels: [],
      timeMachineDensity: [],
      timeMachineMetadata: null,
      timeMachineDensityMetadata: null,
      timeMachineError: null,
    }),
}));
